/** @typedef {import("../../api/types").DeviceTypesPage} DeviceTypesPage */
import React, {useEffect, useState} from "react";
import axios from "axios";
import ClickableRow from "../../shared/components/ClickableRow";
import FieldTags from "./components/FieldTags";
import SearchBox from "../../shared/components/SearchBox";
import SortableTh from "../../shared/components/SortableTh";
import {useDebouncedSearch} from "../../hooks/useDebouncedSearch";
import {useListParams} from "../../hooks/useListParams";
import {useScrollRestore} from "../../hooks/useScrollRestore";
import {Link} from "react-router-dom";
import {Alert, Button, Container, Spinner, Table} from "reactstrap";
import IconLabel from "../../shared/components/IconLabel";
import {ChevronLeftIcon, ChevronRightIcon, PlusIcon} from "../../shared/components/icons";

const DEVICE_TYPES_URL = "/os/api/device_types/";
const DEFAULT_ORDERING = "name"; // by name, A to Z

const DeviceTypes = () => {
    // The list's state lives in the address, so it survives a visit to a device type.
    // `ordering` is the API's value: a field, "-" in front for descending.
    const {page, search, ordering, setPage, setSearch, setOrdering} = useListParams({
        basePath: "/device-types",
        defaultOrdering: DEFAULT_ORDERING,
    });
    // The request waits for a pause in typing; a new search starts from page 1.
    const {text: searchText, setText: setSearchText} = useDebouncedSearch(search, setSearch);
    const [data, setData] = useState(/** @type {DeviceTypesPage|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get(DEVICE_TYPES_URL, {params: {page, search: search || undefined, ordering}, signal: controller.signal})
            .then(({data}) => setData(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar os tipos de equipamento.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [page, search, ordering]);

    useScrollRestore(!loading);

    const sort = {ordering, onChange: setOrdering};

    const emptyMessage = search
        ? `Nenhum tipo de equipamento encontrado para “${search}”.`
        : "Nenhum tipo de equipamento encontrado.";

    const deviceTypes = data?.results ?? [];
    const pagination = data?.pagination;

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <SearchBox
                    value={searchText}
                    onChange={setSearchText}
                    placeholder="Buscar por nome ou descrição"
                    ariaLabel="Buscar tipos de equipamento"
                />
                <Button color="primary" tag={Link} to="/device-types/new">
                    <IconLabel icon={<PlusIcon />}>Criar</IconLabel>
                </Button>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            {/* Fixed layout: the columns keep their share of the row whatever is in
                them (otherwise "Campos" shrinks when the results have fewer tags).
                The minimum width makes narrow screens scroll instead of squeezing
                (on a phone the rows are cards instead, see `table-cards`). */}
            <Table hover responsive className="table-cards" style={{tableLayout: "fixed", minWidth: 640}}>
                <colgroup>
                    <col style={{width: "25%"}} />
                    <col style={{width: "25%"}} />
                    <col style={{width: "50%"}} />
                </colgroup>
                <thead>
                    <tr>
                        <SortableTh field="name" sort={sort}>
                            Nome
                        </SortableTh>
                        <th>Descrição</th>
                        <th>Campos</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr>
                            <td colSpan={3} className="text-center">
                                <Spinner size="sm" />
                            </td>
                        </tr>
                    )}
                    {!loading && !error && deviceTypes.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center text-muted">
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                    {!loading &&
                        deviceTypes.map((deviceType) => (
                            <ClickableRow key={deviceType.id} to={`/device-types/${deviceType.id}`}>
                                <td className="text-break">
                                    <Link to={`/device-types/${deviceType.id}`}>{deviceType.name}</Link>
                                </td>
                                <td className="text-break">{deviceType.description}</td>
                                <td>
                                    <FieldTags
                                        fieldsList={deviceType.fields_list}
                                        empty={<span className="text-muted">—</span>}
                                    />
                                </td>
                            </ClickableRow>
                        ))}
                </tbody>
            </Table>
            {pagination && pagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.prev_page}
                        onClick={() => setPage(pagination.prev_page)}
                    >
                        <IconLabel icon={<ChevronLeftIcon />}>Anterior</IconLabel>
                    </Button>
                    <span>
                        Página {pagination.curr_page} de {pagination.total_pages}
                    </span>
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.next_page}
                        onClick={() => setPage(pagination.next_page)}
                    >
                        <IconLabel icon={<ChevronRightIcon />}>Próxima</IconLabel>
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default DeviceTypes;
